import { ReactNode, useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { faArrowRight, faSync } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import { Box, Button, Columns } from '@domain-group/suit-theme'

import useProviderPriceList, { generateUrls } from '@hooks/useProviderPriceList'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import PageHeader from '@components/PageHeader'

import generateTabBarItems from '../../utils/generateTabBarItems'

interface PageLayoutProps {
  activeTabBarItemKey?: string,
  children: ReactNode,
  providerPriceListPayload: ReturnType<typeof useProviderPriceList>,
  showSavedButton: boolean,
}

const PageLayout = (props: PageLayoutProps) => {
  const {
    activeTabBarItemKey,
    providerPriceListPayload: {
      callbacks: {
        syncChangesToForks: syncChangesFn,
      },
      providerPriceList,
      urls: { providerPriceListsIndexUrl },
    },
    children,
    showSavedButton = false,
  } = props

  const pageContext = useContext(PageContext)
  const { callbacks } = pageContext || {}
  const { syncChangesToForks } = callbacks || {}

  const { redirect } = parsePermittedQueryParams(getQueryParamsFromHash(), ['redirect'])

  const { editProviderPriceListUrl: editMasterPriceListUrl } = generateUrls({
    id: providerPriceList.master_price_list_id,
  })

  const syncChangesToForksPayload = {
    callbacks: {
      action: syncChangesFn,
    },
    toastText: 'Syncing changes to Forks queued',
  }

  const updating = useSelector(reduxState => (
    reduxState.providerPriceListItemOptions.updating
    || reduxState.providerPriceListItemOptionValues.updating
    || reduxState.providerPriceListItems.updating
  ))

  return (
    <>
      <Helmet>
        <title>Edit Price List | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <>
            {showSavedButton && (
              <Button
                buttonStyle="secondaryEdit"
                disabled={!updating}
                loading={updating}
                marginRight="medium"
                size="medium"
                width="auto"
              >
                {updating ? '' : 'Saved'}
              </Button>
            )}

            {!!providerPriceList.master_price_list_id && (
              <Button
                // @ts-expect-error TS2322
                as="a"
                buttonStyle="secondaryEdit"
                href={editMasterPriceListUrl}
                icon={<FontAwesomeIcon icon={faArrowRight} />}
                marginRight="medium"
                size="medium"
                width="auto"
              >
                Go to Master Price List
              </Button>
            )}

            {providerPriceList.is_master && (
              <Button
                // @ts-expect-error TS2322
                as="a"
                buttonStyle="secondaryEdit"
                icon={<FontAwesomeIcon icon={faSync} />}
                onClick={() => syncChangesToForks(syncChangesToForksPayload)}
                marginRight="medium"
                size="medium"
                width="auto"
              >
                Sync Changes
              </Button>
            )}

            <Button
              // @ts-expect-error TS2322
              as="a"
              buttonStyle="secondaryUtility"
              display={['none', 'flex']}
              href={redirect || providerPriceListsIndexUrl}
              size="medium"
              width="auto"
            >
              Back
            </Button>
          </>
        )}
        activeTabBarItemKey={activeTabBarItemKey}
        tabBarItems={generateTabBarItems(providerPriceList)}
        title="Edit Price List"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          {children}
        </Columns>
      </Box>
    </>
  )
}

export default PageLayout
